import { Link } from 'react-router-dom';
import ServiceCard from '../components/ServiceCard';
import { Button } from '../components/ui/button';
import FinancingBanner from '../components/FinancingBanner';
import { Wrench, Cog, Gauge, Car, Paintbrush, Zap, Snowflake, Cpu, Settings2, Sun } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Services() {
	const links = [
		{ name: 'Windshield Repair', to: '/services/windshield-repair', desc: 'AI-powered damage assessment and automated repair process.', icon: <Car size={18} /> },
		{ name: 'Windshield Replacement', to: '/services/windshield-replacement', desc: 'Perfect fit with AI-sourced cost-effective parts.', icon: <Car size={18} /> },
		{ name: 'Side Glass Replacement', to: '/services/side-glass', desc: 'AI-identified exact specifications for any vehicle.', icon: <Car size={18} /> },
		{ name: 'Roof & Back Glass', to: '/services/roof-back-glass', desc: 'Expert installation with automated sourcing.', icon: <Car size={18} /> },
		{ name: 'ADAS Calibrations', to: '/services/adas-calibrations', desc: 'AI-driven diagnostics for accurate recalibrations.', icon: <Cpu size={18} /> },
		{ name: 'Diagnostics & Coding', to: '/services/diagnostics-coding', desc: 'Real-time identification maintaining safety standards.', icon: <Cpu size={18} /> },
	];

	return (
		<div>
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
			className="bg-gradient-to-b from-white to-babyblue/20 border-b gradient-hero"
			>
				<div className="container py-12 md:py-16">
					<motion.h1 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="text-3xl md:text-4xl font-extrabold text-gradient"
					>
						AI-Powered Glass Services
					</motion.h1>
					<motion.p 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.3 }}
						className="mt-3 max-w-3xl text-gray-600"
					>
						Advanced AI technology automates damage assessment, parts sourcing, and technician routing for faster, more cost-effective glass repair and replacement services.
					</motion.p>
					<motion.div 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.4 }}
						className="mt-6 flex gap-3"
					>
						<motion.a 
							href="https://getstarted.ubiquityglass.com" 
							target="_blank" 
							rel="noreferrer"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							<Button variant="gradient">Get Started</Button>
						</motion.a>
						<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
							<Link to="/support"><Button variant="outline">Talk to Sales</Button></Link>
						</motion.div>
					</motion.div>
				</div>
			</motion.section>

			<FinancingBanner />

			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				className="container py-10 md:py-14"
			>
				<motion.div 
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch"
				>
					{links.map((l, index) => (
						<motion.div
							key={l.to}
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
							className="h-full"
						>
							<Link to={l.to} className="block h-full">
								<ServiceCard title={l.name} description={l.desc} icon={l.icon} />
							</Link>
						</motion.div>
					))}
				</motion.div>
			</motion.section>

			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				className="bg-white dark:bg-transparent border-t dark:border-white/10"
			>
				<div className="container py-12 md:py-16 grid md:grid-cols-3 gap-8">
					<motion.div 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						whileHover={{ scale: 1.02 }}
						className="rounded-xl border bg-white p-6 shadow-sm"
					>
						<h3 className="text-lg font-semibold">Mobile Glass Service</h3>
						<p className="mt-2 text-gray-600">Convenient windshield repair and replacement at your location with fully-equipped mobile units.</p>
						<ul className="mt-3 list-disc list-inside text-gray-700 space-y-1">
							<li>Windshield repair and replacement</li>
							<li>Same-day appointments available</li>
							<li>Live tracking and status updates</li>
						</ul>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.3 }}
						whileHover={{ scale: 1.02 }}
						className="rounded-xl border bg-white p-6 shadow-sm"
					>
						<h3 className="text-lg font-semibold">Centralized Expertise</h3>
						<p className="mt-2 text-gray-600">Advanced calibration centers with factory-level equipment for complex glass installations.</p>
						<ul className="mt-3 list-disc list-inside text-gray-700 space-y-1">
							<li>ADAS calibrations and diagnostics</li>
							<li>Complex glass replacements</li>
							<li>Quality control and warranty coverage</li>
						</ul>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.4 }}
						whileHover={{ scale: 1.02 }}
						className="rounded-xl border bg-white p-6 shadow-sm"
					>
						<h3 className="text-lg font-semibold">Why Ubiquity AutoGlass</h3>
						<p className="mt-2 text-gray-600">AI automation, safety standards, and lifetime warranty protection.</p>
						<ul className="mt-3 list-disc list-inside text-gray-700 space-y-1">
							<li>Instant damage assessment and estimates</li>
							<li>NHTSA-compliant installation standards</li>
							<li>Lifetime workmanship warranty</li>
						</ul>
					</motion.div>
				</div>
			</motion.section>

			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				className="animated-gradient particle-container subtle-glow"
			>
				<div className="container py-12 md:py-16 text-white">
					<motion.h3 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="text-2xl font-bold"
					>
						Ready to get your glass fixed?
					</motion.h3>
					<motion.p 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.3 }}
						className="mt-2 max-w-2xl"
					>
						Get a fast, accurate estimate and book in minutes. Our AI-powered system handles damage assessment, parts sourcing, and technician routing.
					</motion.p>
					<motion.div 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.4 }}
						className="mt-6 flex gap-3"
					>
						<motion.a 
							href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" 
							target="_blank" 
							rel="noreferrer"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							<Button className="bg-white text-secondary hover:opacity-90">Start Now</Button>
						</motion.a>
						<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
							<Link to="/support"><Button variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">Contact Sales</Button></Link>
						</motion.div>
					</motion.div>
				</div>
			</motion.section>
		</div>
	);
}
